import { invariant } from '@epic-web/invariant';
import {
  CONFIGURATIONS,
  DELETE,
  PUT,
  logger,
  request,
  useConstant,
  useEndpointsContext,
  useObservableValue,
  useStaticSubscription,
  wsScanToMap,
  type IConfiguration,
  type SubscriptionResponse,
} from '@talos/kyoko';
import { isBoolean, isNumber, toNumber } from 'lodash-es';
import { createContext, useCallback, useContext, useMemo, type PropsWithChildren } from 'react';
import { Subject, merge, shareReplay } from 'rxjs';
import { OrgConfigurationKeys } from './OrgConfigurationKeys';
import { parseOrgConfigBoolean } from './orgConfigValueParsing';
import { useSyncJSProfilingCookie } from './useSyncJSProfilingCookie';

export interface OrgConfigurationContextProps {
  /**
   * Reads the current config.
   * @param path The `Key` of the config.
   * @param defaultValue Value to default to. Function provides returnType equal to defaultValue.
   */
  getConfig(path: OrgConfigurationKey, defaultValue: string): string;

  getConfig(path: OrgConfigurationKey, defaultValue: number): number;

  getConfig(path: OrgConfigurationKey, defaultValue: boolean): boolean;

  /**
   * If you are storing a JSON stringified array in org config, you can grab that using this function.
   * If nothing is set (nullish or empty string), your provided defaultValue will be returned.
   * If something is set but we fail to parse for whatever reason, undefined is returned.
   */
  getJSONArrayConfig: <T>(key: OrgConfigurationKey, defaultValue?: T[]) => T[] | undefined;

  /** Raw configuration values */
  configurations?: Map<string, IConfiguration>;
  enableTrading: () => Promise<SubscriptionResponse<EmptyObject, 'Configuration'>>;
  disableTrading: () => Promise<SubscriptionResponse<EmptyObject, 'Configuration'>>;
  upsert: (configuration: ConfigurationUpsert) => Promise<SubscriptionResponse<IConfiguration, 'Configuration'>>;
  remove: (key: string) => Promise<SubscriptionResponse<IConfiguration, 'Configuration'>>;
  /** Whether or not the provider is loaded and ready to be used. */
  isLoaded: boolean;
}

export const OrgConfigurationContext = createContext<OrgConfigurationContextProps | undefined>(undefined);
OrgConfigurationContext.displayName = 'OrgConfigurationContext';

export function useOrgConfiguration(): OrgConfigurationContextProps {
  const context = useContext(OrgConfigurationContext);
  if (context === undefined) {
    throw new Error('Missing OrgConfiguration.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

/**
 * Provides Organization (per Talos Client) wide settings
 */
export const OrgConfigurationProvider = function OrgConfigurationProvider({ children }: PropsWithChildren) {
  const { orgApiEndpoint } = useEndpointsContext();
  const subject = useConstant(new Subject<SubscriptionResponse<IConfiguration, 'Configuration'>>());
  const { data: subscription } = useStaticSubscription<IConfiguration>({
    name: CONFIGURATIONS,
    tag: 'OrgConfigurationProvider',
  });

  const configurations = useObservableValue(
    () =>
      merge(subscription, subject).pipe(
        wsScanToMap({
          getUniqueKey: item => item.Key,
          newMapEachUpdate: true,
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [subscription, subject]
  );

  const isLoaded = useMemo(() => configurations !== undefined, [configurations]);

  const getConfig = useCallback<OrgConfigurationContextProps['getConfig']>(
    function <T extends string | number | boolean>(path: string, defaultValue: T): T {
      return getConfigOrDefault<T>(configurations, path, defaultValue);
    },
    [configurations]
  );

  const getJSONArrayConfig = useCallback(
    <T,>(key: OrgConfigurationKey, defaultValue?: T[]) => {
      const stringifiedArray = getConfig(key, '');

      if (stringifiedArray === '') {
        // No value set, return default value (might be undefined)
        return defaultValue;
      }

      try {
        const array: T[] = JSON.parse(stringifiedArray);
        invariant(Array.isArray(array), `${array} is not an array`);
        return array;
      } catch (e) {
        logger.error(new Error(`Failed to parse ${key}`), {
          extra: { input: stringifiedArray },
        });
        // We return undefined here instead of defaultValue. Something other than the default has been set, but cannot be parsed.
        // Returning the defaultValue might be dangerous. We could parameterize this in the future.
        return undefined;
      }
    },
    [getConfig]
  );

  useSyncJSProfilingCookie(isLoaded, getConfig);

  const enableTrading = useCallback(() => {
    return request(PUT, `${orgApiEndpoint}/enable-trading`);
  }, [orgApiEndpoint]);

  const disableTrading = useCallback(() => {
    return request(PUT, `${orgApiEndpoint}/disable-trading`);
  }, [orgApiEndpoint]);

  const upsert = useCallback(
    (configuration: ConfigurationUpsert) => {
      return request(PUT, `${orgApiEndpoint}/configurations`, configuration).then(json => {
        subject.next(json);
        return json;
      });
    },
    [orgApiEndpoint, subject]
  );

  const remove = useCallback(
    (key: string) => {
      return request(DELETE, `${orgApiEndpoint}/configurations`, { Key: key } satisfies ConfigurationRemove).then(
        json => {
          subject.next(json);
          return json;
        }
      );
    },
    [orgApiEndpoint, subject]
  );

  const value = useMemo(() => {
    return {
      getConfig,
      getJSONArrayConfig,
      configurations,
      upsert,
      remove,
      enableTrading,
      disableTrading,
      isLoaded,
    };
  }, [getConfig, getJSONArrayConfig, configurations, upsert, remove, enableTrading, disableTrading, isLoaded]);

  return <OrgConfigurationContext.Provider value={value}>{children}</OrgConfigurationContext.Provider>;
};

export function getConfigOrDefault<T extends string | number | boolean>(
  configurations: Map<string, IConfiguration> | undefined,
  path: string,
  defaultValue: T
): T {
  try {
    if (!configurations) {
      return defaultValue;
    }
    if (isNumber(defaultValue)) {
      const out = toNumber(configurations.get(path)?.Value);
      return out === undefined || isNaN(out) ? defaultValue : (out as T);
    }
    if (isBoolean(defaultValue)) {
      return parseOrgConfigBoolean(configurations.get(path)?.Value, defaultValue) as T;
    }
    const out = configurations.get(path)?.Value;
    return out === undefined ? defaultValue : (out as T);
  } catch (e) {
    logger.error(e as Error);
  }
  return defaultValue;
}

export interface OrgConfiguration {
  Key: OrgConfigurationKey;
  Value: string | number | boolean;
}

export interface ConfigurationUpsert {
  Key: string;
  Value: string;
}

export interface ConfigurationRemove {
  Key: string;
}

// Derived from OrgConfigurationKeys — maps each spec name to its key string.
// This is a drop-in replacement for the previous enum: all call sites using
// OrgConfigurationKey.SomeName continue to work unchanged.
export const OrgConfigurationKey = Object.fromEntries(
  Object.entries(OrgConfigurationKeys).map(([name, spec]) => [name, spec.key])
) as { [K in keyof typeof OrgConfigurationKeys]: (typeof OrgConfigurationKeys)[K]['key'] };

export type OrgConfigurationKey = (typeof OrgConfigurationKey)[keyof typeof OrgConfigurationKey];
